import { SHIP_HOLD_FIRE_INTERVAL_MS } from 'asteroids-shared/constants'

const KEY_CODES = {
  left: ['ArrowLeft', 'KeyA'],
  right: ['ArrowRight', 'KeyD'],
  up: ['ArrowUp', 'KeyW'],
  down: ['ArrowDown', 'KeyS'],
  space: ['Space']
}

const TRACKED = new Set(Object.values(KEY_CODES).flat())
const SPACE_CODES = new Set(KEY_CODES.space)

export default class Keyboard {
  #onKeyDown = (e) => {
    if (!TRACKED.has(e.code)) return
    e.preventDefault()
    if (e.repeat) return
    this.onActivity?.()
    if (SPACE_CODES.has(e.code) && !this.#spaceHeld()) {
      this.firePresses += 1
      this.lastFireEmitMs = performance.now()
    }
    this.#set(e.code, true)
  }

  #onKeyUp = (e) => {
    if (e.code === 'MetaLeft' || e.code === 'MetaRight' || e.key === 'Meta') {
      this.release()
      return
    }
    if (!TRACKED.has(e.code)) return
    e.preventDefault()
    this.#set(e.code, false)
  }

  #onBlur = () => this.release()

  #onVisibilityChange = () => {
    if (document.hidden) this.release()
  }

  constructor({ onActivity } = {}) {
    this.onActivity = onActivity
    this.keys = { left: false, right: false, up: false, down: false }
    this.active = new Set()
    this.changeOrder = new Map()
    this.changeSeq = 0
    // Discrete fire-press counter; consumed (zeroed) on poll().
    this.firePresses = 0
    // Wall-clock of the last emitted press (real or synthesized) — gates held auto-repeat.
    this.lastFireEmitMs = -Infinity
  }

  start() {
    window.addEventListener('keydown', this.#onKeyDown)
    window.addEventListener('keyup', this.#onKeyUp)
    window.addEventListener('blur', this.#onBlur)
    document.addEventListener('visibilitychange', this.#onVisibilityChange)
  }

  stop() {
    window.removeEventListener('keydown', this.#onKeyDown)
    window.removeEventListener('keyup', this.#onKeyUp)
    window.removeEventListener('blur', this.#onBlur)
    document.removeEventListener('visibilitychange', this.#onVisibilityChange)
    this.release()
  }

  poll() {
    if (this.#spaceHeld()) {
      const now = performance.now()
      if (now - this.lastFireEmitMs >= SHIP_HOLD_FIRE_INTERVAL_MS) {
        this.firePresses += 1
        this.lastFireEmitMs = now
      }
    }
    const out = {
      left: this.keys.left,
      right: this.keys.right,
      up: this.keys.up,
      down: this.keys.down,
      firePresses: this.firePresses
    }
    this.firePresses = 0
    return out
  }

  release() {
    this.firePresses = 0
    this.lastFireEmitMs = -Infinity
    if (this.active.size === 0 && !Object.values(this.keys).some(Boolean)) return
    this.active.clear()
    this.changeOrder.clear()
    this.keys.left = false
    this.keys.right = false
    this.keys.up = false
    this.keys.down = false
  }

  #spaceHeld() {
    for (const code of KEY_CODES.space) {
      if (this.active.has(code)) return true
    }
    return false
  }

  #set(code, isDown) {
    const was = this.active.has(code)
    if (was === isDown) return

    if (isDown) {
      this.active.add(code)
      this.changeOrder.set(code, ++this.changeSeq)
    } else {
      this.active.delete(code)
      this.changeOrder.delete(code)
    }

    this.#recompute()
  }

  #latestActive(codes) {
    let latest = 0
    for (const code of codes) {
      if (!this.active.has(code)) continue
      latest = Math.max(latest, this.changeOrder.get(code) ?? 0)
    }
    return latest
  }

  #recompute() {
    const leftOrder = this.#latestActive(KEY_CODES.left)
    const rightOrder = this.#latestActive(KEY_CODES.right)
    this.keys.left = leftOrder > rightOrder
    this.keys.right = rightOrder > leftOrder
    this.keys.up = KEY_CODES.up.some(code => this.active.has(code))
    this.keys.down = KEY_CODES.down.some(code => this.active.has(code))
  }
}
